import { createFileRoute, notFound } from "@tanstack/react-router";

import { BandLink, SealButton, WhatsAppLink } from "@/components/site/ctas";
import { Link } from "@/components/site/link";
import { Reveal } from "@/components/site/motion";
import { getDict, href, type Lang } from "@/content/site";

export const Route = createFileRoute("/$lang/markets/$slug")({
  loader: ({ params }) => {
    const lang = params.lang as Lang;
    const market = getDict(lang).markets.find((m) => m.slug === params.slug);
    if (!market) throw notFound();
    return { lang, market };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { market } = loaderData;
    return {
      meta: [
        { title: `${market.name} · insidermark` },
        { name: "description", content: market.why },
      ],
    };
  },
  component: Page,
});

function Page() {
  const { lang, market: m } = Route.useLoaderData();
  const t = getDict(lang);
  const p = t.marketPage;
  const divisions = t.divisions.filter((d) => m.divisions.includes(d.slug));
  return (
    <main>
      <header className="im-page-head">
        <div className="im-wrap">
          <Reveal>
            <Link className="im-mono" to={href(lang, "/markets")}>
              ← {p.all}
            </Link>
            <p className="im-eyebrow mt-6">{m.region}</p>
            <h1 className="im-display mt-3">{m.name}</h1>
            <p className="im-body-text mt-5">{m.why}</p>
            {m.phase === 2 ? <p className="im-mono mt-4">{p.phase2}</p> : null}
          </Reveal>
        </div>
      </header>

      <section className="im-section im-section--tight">
        <div className="im-wrap">
          <h2 className="im-h3 mb-6">{p.logistics}</h2>
          <dl className="im-facts">
            <div>
              <dt>{p.origin}</dt>
              <dd>{m.logistics.origin}</dd>
            </div>
            <div>
              <dt>{p.destination}</dt>
              <dd>{m.logistics.destination}</dd>
            </div>
            <div>
              <dt>{p.transit}</dt>
              <dd>{m.logistics.transit}</dd>
            </div>
            <div>
              <dt>{p.language}</dt>
              <dd>{m.language}</dd>
            </div>
          </dl>
        </div>
      </section>

      <section className="im-section">
        <div className="im-plate im-plate--manifest" aria-hidden="true" />
        <div className="im-wrap im-rel im-two">
          <Reveal>
            <h2 className="im-display-sm">{p.importRules}</h2>
            <p className="im-acc__text mt-4">{p.importNote}</p>
          </Reveal>
          <ul className="im-list">
            {m.importRules.map((r) => (
              <li key={r}>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="im-section im-section--tight">
        <div className="im-wrap im-two">
          <div>
            <h2 className="im-display-sm">{p.activeDivisions}</h2>
            <p className="im-mono mt-6">{p.reference}</p>
            <p className="im-acc__text mt-2">{m.reference}</p>
          </div>
          <div>
            {divisions.map((d) => (
              <BandLink key={d.slug} to={href(lang, `/divisions/${d.slug}`)} meta={`DIV ${d.code}`}>
                {d.name}
              </BandLink>
            ))}
          </div>
        </div>
      </section>

      <section className="im-section">
        <div className="im-wrap im-closing">
          <h2 className="im-display-sm max-w-[18ch]">{p.rfq}</h2>
          <div className="im-closing__ctas">
            <SealButton to={href(lang, `/rfq?country=${encodeURIComponent(m.name)}`)}>{t.nav.rfq}</SealButton>
            <WhatsAppLink label={t.nav.whatsapp} text={`Hello Insidermark, quotation request from ${m.name}.`} />
          </div>
        </div>
      </section>
    </main>
  );
}
